import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LoopIcon from "@mui/icons-material/Loop";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import WavingHandOutlinedIcon from "@mui/icons-material/WavingHandOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import type { ElementType, FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  HOSTED_NIGHTSCOUT_AICHAT_MONTHLY_EUR,
  HostedNightscoutPriceDisplay,
  type HostedNightscoutBillingCycle,
} from "src/sections/pricing/hosted-nightscout-billing";

export type ServicePlanBillingPlan = "hosted" | "hosted-aichat";
export type ServicePlanBadge = "nen" | "medical";

export interface ServicePlanCardData {
  id: string;
  category: string;
  title: string;
  description: string;
  price: string;
  details: string[];
  buttonText: string;
  buttonVariant: "contained" | "outlined";
  illustrationBg: string;
  link: string;
  billingPlan?: ServicePlanBillingPlan;
  badge?: ServicePlanBadge;
}

interface ServicePlanCardProps {
  service: ServicePlanCardData;
  billingCycle: HostedNightscoutBillingCycle;
  illustration?: ReactNode;
}

export const ServicePlanCard: FC<ServicePlanCardProps> = ({
  service,
  billingCycle,
  illustration,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: 3,
        p: { xs: 3, md: 2.5 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        border: "1px solid #F3F4F6",
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
        },
      }}
    >
      {illustration ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {illustration}
        </Box>
      ) : null}

      <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
        <Chip
          label={t(service.category)}
          size="small"
          sx={{
            backgroundColor: "#F3F4F6",
            color: "#6B7280",
            fontWeight: "500",
            fontSize: "0.75rem",
          }}
        />
      </Box>

      <Typography
        variant="h5"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: "bold",
          mb: 2,
          textAlign: "center",
          lineHeight: 1.3,
        }}
      >
        {t(service.title)}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          mb: 3,
          textAlign: "center",
          lineHeight: 1.6,
        }}
      >
        {t(service.description)}
      </Typography>

      {service.badge ? (
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Chip
            label={
              service.badge === "medical"
                ? t("Medically supervised")
                : t("Hosted on NEN 7510-certified infrastructure")
            }
            size="small"
            sx={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              fontWeight: "600",
              fontSize: "0.75rem",
              "& .MuiChip-label": {
                px: 1.5,
              },
            }}
          />
        </Box>
      ) : null}

      <Box
        sx={{
          height: "1px",
          backgroundColor: "#E5E7EB",
          mb: 3,
        }}
      />

      <Box sx={{ mb: 4, flexGrow: 1 }}>
        {service.details.map((detail) => (
          <Box
            key={detail}
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
            }}
          >
            <ArrowForwardIcon
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "16px",
                mr: 1,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              {t(detail)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: "auto" }}>
        <Box sx={{ mb: 2, textAlign: "center" }}>
          {service.billingPlan ? (
            <HostedNightscoutPriceDisplay
              billingCycle={billingCycle}
              variant="h6"
              monthlyEur={
                service.billingPlan === "hosted-aichat"
                  ? HOSTED_NIGHTSCOUT_AICHAT_MONTHLY_EUR
                  : undefined
              }
            />
          ) : (
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: "bold",
              }}
            >
              {service.price ? t(service.price) : ""}
            </Typography>
          )}
        </Box>
        <Button
          href={service.link}
          target="_blank"
          rel="noopener noreferrer"
          variant={service.buttonVariant}
          fullWidth
          size="large"
          sx={{
            backgroundColor:
              service.buttonVariant === "contained"
                ? theme.palette.primary.main
                : "transparent",
            color:
              service.buttonVariant === "contained"
                ? theme.palette.primary.contrastText
                : theme.palette.text.primary,
            borderColor:
              service.buttonVariant === "outlined" ? "#E5E7EB" : "transparent",
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: "600",
            py: 1.5,
            "&:hover": {
              backgroundColor:
                service.buttonVariant === "contained"
                  ? theme.palette.primary.dark
                  : "#F9FAFB",
              borderColor:
                service.buttonVariant === "outlined" ? "#D1D5DB" : "#E5E7EB",
            },
          }}
        >
          {t(service.buttonText)}
        </Button>
      </Box>
    </Box>
  );
};

const PLAN_ICONS: Record<
  string,
  { Icon: ElementType; color: string; background: string }
> = {
  onboarding: {
    Icon: WavingHandOutlinedIcon,
    color: "#D97706",
    background: "#FEF3C7",
  },
  technology: {
    Icon: CloudOutlinedIcon,
    color: "#2563EB",
    background: "#DBEAFE",
  },
  "technology-aichat": {
    Icon: ChatOutlinedIcon,
    color: "#4F46E5",
    background: "#E0E7FF",
  },
  coaching: {
    Icon: MedicalServicesOutlinedIcon,
    color: "#DB2777",
    background: "#FCE7F3",
  },
  carbless: {
    Icon: LoopIcon,
    color: "#059669",
    background: "#D1FAE5",
  },
  "home-visit": {
    Icon: HomeOutlinedIcon,
    color: "#0891B2",
    background: "#CFFAFE",
  },
  "loop-config-short": {
    Icon: TuneOutlinedIcon,
    color: "#7C3AED",
    background: "#EDE9FE",
  },
  "loop-config-long": {
    Icon: TuneOutlinedIcon,
    color: "#C2410C",
    background: "#FFEDD5",
  },
};

export function getServicePlanIllustration(serviceId: string): ReactNode {
  const plan = PLAN_ICONS[serviceId];
  if (!plan) {
    return null;
  }

  const { Icon, color, background } = plan;

  return (
    <Box
      aria-hidden
      sx={{
        width: 72,
        height: 72,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: background,
        color,
      }}
    >
      <Icon sx={{ fontSize: 34 }} />
    </Box>
  );
}
